import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  apiTicketingHoldPolicy,
  apiTicketingHoldPolicyOverrides,
  apiTicketingUpdatePlatformHoldPolicy,
  apiTicketingUpdateOrganizerHoldPolicy,
  apiTicketingMyOrganizers,
} from "../../api";
import { getUserFromLocalStorage } from "../../utilities/user";
import { Panel, Field, PInput, PSelect, PButton, S, C } from "../../theme/pbi";
import SettingHelp from "../../views/Settings/settingsHelp";

/**
 * HoldPolicyPanel — the seat-hold dial, editable live.
 *
 * Layered, most specific first: showing -> event -> organizer -> platform
 * default -> server config -> built-in default. Whatever wins is clamped into
 * the min/max band a super admin sets, so an organizer can tune their own
 * events without being able to take the platform somewhere that breaks
 * checkout or freezes the house. What each field really does, including the
 * payment window that online checkout does not read yet, is in
 * views/Settings/help/ticketingHelp.js; the hints quote the server's own
 * defaults and bands from the GET (reference, bands).
 *
 * House rule: <Table responsive> does nothing here (the dashboard theme sets
 * .table-responsive { overflow: visible } at >= 992px), so the overrides table
 * lives in its own overflow-x container.
 */

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const isSuperAdminProfile = () => {
  const profile = getUserFromLocalStorage();
  const result = profile?.result || {};
  const role = String(result.user_role || result.role || "").toUpperCase();
  return Boolean(result.isSuperAdmin) || ["SUPERUSER", "SUPERADMIN"].includes(role);
};

const LAYER_LABEL = {
  REQUEST: "Requested by the caller",
  SHOWING: "Showing",
  SHOWING_CHANNEL: "Showing (box office)",
  EVENT: "Event",
  EVENT_CHANNEL: "Event (box office)",
  ORGANIZER: "Organizer",
  ORGANIZER_CHANNEL: "Organizer (box office)",
  PLATFORM: "Platform default",
  PLATFORM_CHANNEL: "Platform default (box office)",
  ENV: "Server configuration",
  SHIPPED: "Built-in default",
  NONE: "Not set",
};

const Stat = ({ label, value, unit, note, color = C.green }) => (
  <div style={{ ...S.kpi(color), minWidth: 190, flex: "1 1 190px" }}>
    <div style={S.kpiVal}>
      {value}
      {unit ? <span style={{ fontSize: 13, fontWeight: 600, color: C.textLight, marginLeft: 5 }}>{unit}</span> : null}
    </div>
    <div style={S.kpiLabel}>{label}</div>
    {note ? <div style={{ fontSize: 11, color: C.textMid, marginTop: 6, lineHeight: 1.4 }}>{note}</div> : null}
  </div>
);

const HoldPolicyPanel = () => {
  const superAdmin = useMemo(isSuperAdminProfile, []);

  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Platform defaults + the band the super admin enforces.
  const [form, setForm] = useState({
    holdTtlMinutes: "",
    boxOfficeHoldTtlMinutes: "",
    checkoutGraceMinutes: "",
    partnerBlockTtlMinutes: "",
    holdMin: "",
    holdMax: "",
    partnerMin: "",
    partnerMax: "",
  });

  // Per-organizer overrides.
  const [overrides, setOverrides] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [newOverride, setNewOverride] = useState({ organizerId: "", minutes: "" });
  const [savingOrganizerId, setSavingOrganizerId] = useState(null);

  // "Why did this showing expire when it did?"
  const [lookupId, setLookupId] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const hydrate = useCallback((data) => {
    setPolicy(data);
    setForm({
      holdTtlMinutes: data?.overrides?.platform?.holdTtlMinutes ?? "",
      boxOfficeHoldTtlMinutes: data?.overrides?.platform?.boxOfficeHoldTtlMinutes ?? "",
      checkoutGraceMinutes: data?.overrides?.platform?.checkoutGraceMinutes ?? "",
      partnerBlockTtlMinutes: data?.overrides?.platform?.partnerBlockTtlMinutes ?? "",
      holdMin: data?.bands?.holdTtl?.min ?? "",
      holdMax: data?.bands?.holdTtl?.max ?? "",
      partnerMin: data?.bands?.partnerBlockTtl?.min ?? "",
      partnerMax: data?.bands?.partnerBlockTtl?.max ?? "",
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiTicketingHoldPolicy();
      hydrate(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load the hold policy.");
    } finally {
      setLoading(false);
    }
    try {
      const [ov, orgs] = await Promise.all([
        apiTicketingHoldPolicyOverrides(),
        apiTicketingMyOrganizers(),
      ]);
      setOverrides(ov?.data?.overrides || []);
      setOrganizers(orgs?.data?.organizers || []);
    } catch (err) {
      // Non-fatal: the platform dial above is still usable without the table.
      setOverrides([]);
    }
  }, [hydrate]);

  useEffect(() => { load(); }, [load]);

  const savePlatform = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        holdTtlMinutes: num(form.holdTtlMinutes),
        boxOfficeHoldTtlMinutes: num(form.boxOfficeHoldTtlMinutes),
        checkoutGraceMinutes: num(form.checkoutGraceMinutes),
        partnerBlockTtlMinutes: num(form.partnerBlockTtlMinutes),
      };
      if (superAdmin) {
        body.bands = {
          holdTtl: { min: Number(form.holdMin), max: Number(form.holdMax) },
          partnerBlockTtl: { min: Number(form.partnerMin), max: Number(form.partnerMax) },
        };
      }
      const { data } = await apiTicketingUpdatePlatformHoldPolicy(body);
      hydrate(data);
      toast.success(`Seat holds now last ${data.holdTtlMinutes} minutes by default.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update the hold policy.");
    } finally {
      setSaving(false);
    }
  };

  const saveOrganizer = async (organizerId, minutes) => {
    setSavingOrganizerId(organizerId);
    try {
      await apiTicketingUpdateOrganizerHoldPolicy(organizerId, { holdTtlMinutes: num(minutes) });
      toast.success(minutes === "" || minutes === null ? "Override cleared." : `Override saved: ${minutes} minutes.`);
      setNewOverride({ organizerId: "", minutes: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the override.");
    } finally {
      setSavingOrganizerId(null);
    }
  };

  const runLookup = async (e) => {
    e.preventDefault();
    if (!lookupId.trim()) return;
    setLookingUp(true);
    try {
      const { data } = await apiTicketingHoldPolicy({ showingId: lookupId.trim() });
      setLookup(data);
    } catch (err) {
      setLookup(null);
      toast.error(err?.response?.data?.message || "Could not resolve that showing.");
    } finally {
      setLookingUp(false);
    }
  };

  const band = policy?.bands?.holdTtl;
  const partnerBand = policy?.bands?.partnerBlockTtl;
  const checkoutBand = policy?.bands?.checkoutGrace;
  // What a blank field falls back to: the hosting environment, else the
  // server's shipped default. Both come from the GET, never typed here.
  const reference = policy?.reference || {};
  const serverDefault = (field) => reference.env?.[field] ?? reference.shippedDefaults?.[field] ?? null;
  const shippedBands = reference.shippedBands || {};
  const hardLimits = reference.hardLimits || {};
  const bandHint = (name, end) =>
    shippedBands[name] && hardLimits[name]
      ? `Default ${shippedBands[name][end]}; the server never allows ${end === "min" ? "below" : "above"} ${hardLimits[name][end]}.`
      : null;
  const resolution = policy?.resolution?.holdTtlMinutes;
  const overrideOrganizerIds = new Set(overrides.map((o) => String(o.organizerId)));
  const availableOrganizers = organizers.filter((o) => !overrideOrganizerIds.has(String(o._id)));

  return (
    <>
      <Panel
        title="Seat hold time"
        sub={band ? `allowed range ${band.min} to ${band.max} minutes` : null}
        color={C.blue}
      >
        {loading ? (
          <div style={{ color: C.textLight }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
              <Stat
                label={<>Seat hold in force<SettingHelp topic="ticketingHoldLadder" /></>}
                value={policy?.holdTtlMinutes ?? "-"}
                unit="min"
                note={resolution ? `Source: ${LAYER_LABEL[resolution.source] || resolution.source}` : null}
                color={C.blue}
              />
              <Stat
                label={<>Payment window at checkout<SettingHelp topic="ticketingCheckoutWindow" /></>}
                value={policy?.checkoutGraceMinutes ?? "-"}
                unit="min"
                note="Saved but not applied yet: online checkout extends holds by a fixed time."
                color={C.teal}
              />
              <Stat
                label={<>Partner seat lease<SettingHelp topic="ticketingPartnerLease" /></>}
                value={policy?.partnerBlockTtlMinutes ?? "-"}
                unit="min"
                note={partnerBand ? `Its own range, ${partnerBand.min} to ${partnerBand.max} minutes, never more than 24 hours.` : null}
                color={C.purple}
              />
            </div>

            <form onSubmit={savePlatform}>
              <div style={S.sectionBreak}>Platform defaults (super administrator only)</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                <Field
                  label={<>Online seat hold (minutes)<SettingHelp topic="ticketingOnlineHold" /></>}
                  hint={band ? `Between ${band.min} and ${band.max}. Blank uses the server default of ${serverDefault("holdTtlMinutes")} minutes.` : null}
                  style={{ flex: "1 1 190px" }}
                >
                  <PInput
                    type="number"
                    value={form.holdTtlMinutes}
                    onChange={(e) => setForm({ ...form, holdTtlMinutes: e.target.value })}
                    placeholder={String(policy?.reference?.shippedDefaults?.holdTtlMinutes ?? "")}
                  />
                </Field>
                <Field
                  label={<>Box office seat hold (minutes)<SettingHelp topic="ticketingBoxOfficeHold" /></>}
                  hint="Seats reserved at a POS terminal counter. Blank uses the online seat hold."
                  style={{ flex: "1 1 190px" }}
                >
                  <PInput
                    type="number"
                    value={form.boxOfficeHoldTtlMinutes}
                    onChange={(e) => setForm({ ...form, boxOfficeHoldTtlMinutes: e.target.value })}
                    placeholder="same as online"
                  />
                </Field>
                <Field
                  label={<>Payment window at checkout (minutes)<SettingHelp topic="ticketingCheckoutWindow" /></>}
                  hint={`Saved but not applied yet: online checkout uses a fixed time. Blank uses ${serverDefault("checkoutGraceMinutes")} minutes${checkoutBand ? `; allowed ${checkoutBand.min} to ${checkoutBand.max}` : ""}.`}
                  style={{ flex: "1 1 190px" }}
                >
                  <PInput
                    type="number"
                    value={form.checkoutGraceMinutes}
                    onChange={(e) => setForm({ ...form, checkoutGraceMinutes: e.target.value })}
                    placeholder={String(policy?.reference?.shippedDefaults?.checkoutGraceMinutes ?? "")}
                  />
                </Field>
                <Field
                  label={<>Partner seat lease (minutes)<SettingHelp topic="ticketingPartnerLease" /></>}
                  hint={`Seats blocked through the partner API when the partner names no time. Blank uses ${serverDefault("partnerBlockTtlMinutes")} minutes${partnerBand ? `; allowed ${partnerBand.min} to ${partnerBand.max}, never over 24 hours` : ""}.`}
                  style={{ flex: "1 1 190px" }}
                >
                  <PInput
                    type="number"
                    value={form.partnerBlockTtlMinutes}
                    onChange={(e) => setForm({ ...form, partnerBlockTtlMinutes: e.target.value })}
                    placeholder={String(policy?.reference?.shippedDefaults?.partnerBlockTtlMinutes ?? "")}
                  />
                </Field>
              </div>

              {superAdmin && (
                <>
                  <div style={S.sectionBreak}>Allowed range (super administrator only)</div>
                  <div style={{ fontSize: 12.5, color: C.textMid, marginBottom: 12, lineHeight: 1.5 }}>
                    Every seat hold and partner lease, at platform, organizer or showing level, is kept
                    inside these limits. A value outside them is refused when it is typed and pulled to
                    the nearest limit when a hold is made. The payment window&apos;s range is set on the
                    server and is not edited here.
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                    <Field label={<>Shortest seat hold (minutes)<SettingHelp topic="ticketingHoldRange" /></>} hint={bandHint("holdTtl", "min")} style={{ flex: "1 1 150px" }}>
                      <PInput type="number" value={form.holdMin} onChange={(e) => setForm({ ...form, holdMin: e.target.value })} />
                    </Field>
                    <Field label="Longest seat hold (minutes)" hint={bandHint("holdTtl", "max")} style={{ flex: "1 1 150px" }}>
                      <PInput type="number" value={form.holdMax} onChange={(e) => setForm({ ...form, holdMax: e.target.value })} />
                    </Field>
                    <Field label={<>Shortest partner lease (minutes)<SettingHelp topic="ticketingPartnerLeaseRange" /></>} hint={bandHint("partnerBlockTtl", "min")} style={{ flex: "1 1 150px" }}>
                      <PInput type="number" value={form.partnerMin} onChange={(e) => setForm({ ...form, partnerMin: e.target.value })} />
                    </Field>
                    <Field label="Longest partner lease (minutes)" hint={bandHint("partnerBlockTtl", "max")} style={{ flex: "1 1 150px" }}>
                      <PInput type="number" value={form.partnerMax} onChange={(e) => setForm({ ...form, partnerMax: e.target.value })} />
                    </Field>
                  </div>
                </>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 6 }}>
                <PButton type="submit" loading={saving}>Save hold policy</PButton>
                <span style={{ fontSize: 12, color: C.textLight }}>
                  Applies to holds made after saving, on every server within 30 seconds. Holds already running keep their expiry.
                </span>
              </div>
            </form>
          </>
        )}
      </Panel>

      <Panel title="Choosing a hold time" color={C.orange}>
        <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.65 }}>
          <p style={{ marginBottom: 10 }}>
            A hold is the window between a customer picking their seats and paying for them. While the
            hold lasts, nobody else can buy those seats.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "1 1 260px", background: C.orangeSoft, border: "1px solid #ffe0b2", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>Shorter hold</div>
              Seats come back on sale faster, which matters on a show that is nearly sold out. The cost
              is failed checkouts: a customer waiting on a mobile-money prompt can run out of time and
              lose the seats they had already chosen.
            </div>
            <div style={{ flex: "1 1 260px", background: C.blueSoft, border: "1px solid #cfe2ff", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>Longer hold</div>
              Almost every customer finishes paying. The cost is phantom unavailability: during a rush,
              seats that nobody ends up buying sit locked, the map looks sold out, and other buyers
              leave.
            </div>
          </div>
          <p style={{ marginTop: 12, marginBottom: 0 }}>
            A practical starting point is 8 to 12 minutes for mobile money markets, shorter for a
            box office counter where the customer is standing in front of you, and longer only for a
            high value seated event where abandoned checkouts are expensive. The payment window at
            checkout is meant to be a separate, longer timer that starts when payment begins, so a
            generous payment window would not force a generous browsing hold. Online checkout does not
            read the saved payment window yet; it extends the hold by a fixed time when payment starts.
          </p>
        </div>
      </Panel>

      <Panel title={<>Organizer overrides<SettingHelp topic="ticketingOrganizerOverrides" /></>} sub={`${overrides.length} set`} color={C.green}>
        {/* Own scroll container on purpose: <Table responsive> is inert in this
            theme (.table-responsive is overridden to overflow: visible >= 992px). */}
        <div style={{ overflowX: "auto", width: "100%", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <thead>
              <tr>
                <th style={S.th}>Organizer</th>
                <th style={S.th}>Online seat hold</th>
                <th style={S.th}>Partner seat lease</th>
                <th style={S.th}>Updated</th>
                <th style={{ ...S.th, textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {overrides.length === 0 && (
                <tr>
                  <td style={{ ...S.td, color: C.textLight }} colSpan={5}>
                    No organizer overrides. Every organizer follows the platform default above.
                  </td>
                </tr>
              )}
              {overrides.map((o) => (
                <tr key={o.organizerId}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{o.organizerName}</div>
                    <div style={{ fontSize: 11, color: C.textLight }}>{o.organizerId}</div>
                  </td>
                  <td style={S.td}>
                    {o.holdTtlMinutes != null ? (
                      <span style={S.badge(C.blue, C.blueSoft)}>{o.holdTtlMinutes} min</span>
                    ) : (
                      <span style={{ color: C.textLight }}>platform default</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {o.partnerBlockTtlMinutes != null ? `${o.partnerBlockTtlMinutes} min` : <span style={{ color: C.textLight }}>platform default</span>}
                  </td>
                  <td style={{ ...S.td, color: C.textLight, fontSize: 12 }}>
                    {o.updatedAt ? new Date(o.updatedAt).toLocaleString() : "-"}
                  </td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <PButton
                      variant="ghost"
                      loading={savingOrganizerId === o.organizerId}
                      onClick={() => saveOrganizer(o.organizerId, "")}
                      style={{ height: 32, padding: "0 12px", fontSize: 12 }}
                    >
                      Clear
                    </PButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={S.sectionBreak}>Add an override</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <Field label="Organizer" style={{ flex: "2 1 240px", marginBottom: 0 }}>
            <PSelect
              value={newOverride.organizerId}
              onChange={(e) => setNewOverride({ ...newOverride, organizerId: e.target.value })}
            >
              <option value="">Select an organizer</option>
              {availableOrganizers.map((o) => (
                <option key={o._id} value={o._id}>{o.name}</option>
              ))}
            </PSelect>
          </Field>
          <Field
            label="Online seat hold (minutes)"
            hint={band ? `Between ${band.min} and ${band.max}.` : null}
            style={{ flex: "1 1 150px", marginBottom: 0 }}
          >
            <PInput
              type="number"
              value={newOverride.minutes}
              onChange={(e) => setNewOverride({ ...newOverride, minutes: e.target.value })}
            />
          </Field>
          <PButton
            variant="outline"
            disabled={!newOverride.organizerId || newOverride.minutes === ""}
            loading={savingOrganizerId === newOverride.organizerId}
            onClick={() => saveOrganizer(newOverride.organizerId, newOverride.minutes)}
            style={{ marginBottom: 0 }}
          >
            Save override
          </PButton>
        </div>
      </Panel>

      <Panel title={<>Check one showing<SettingHelp topic="ticketingShowingLookup" /></>} sub="why a hold expires when it does" color={C.teal}>
        <form onSubmit={runLookup} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
          <Field label="Showing ID" style={{ flex: "2 1 300px", marginBottom: 0 }}>
            <PInput value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder="Paste a showing id" />
          </Field>
          <PButton type="submit" variant="outline" loading={lookingUp}>Resolve</PButton>
        </form>

        {lookup && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                background: C.tealSoft,
                border: "1px solid #b2ebf2",
                color: C.text,
                borderRadius: 8,
                padding: "11px 14px",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {lookup.summary?.holdTtlMinutes}
            </div>
            <div style={{ overflowX: "auto", width: "100%", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 460 }}>
                <thead>
                  <tr>
                    <th style={S.th}>Layer</th>
                    <th style={S.th}>Value</th>
                    <th style={S.th}>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {(lookup.resolution?.holdTtlMinutes?.trace || [])
                    .filter((t) => t.value != null || t.applied)
                    .map((t, i) => (
                      <tr key={`${t.source}-${i}`}>
                        <td style={S.td}>{LAYER_LABEL[t.source] || t.source}</td>
                        <td style={S.td}>{t.value != null ? `${t.value} min` : "-"}</td>
                        <td style={S.td}>
                          {t.applied ? <span style={S.badge(C.green, C.greenSoft)}>winner</span> : <span style={{ color: C.textLight }}>-</span>}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Panel>
    </>
  );
};

export default HoldPolicyPanel;
